import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars'
import EventEmitter, {
  SCROLL,
  SCROLL_DISABLE,
  SCROLL_DOWN,
  SCROLL_TO_TOP,
} from '../../helpers/EventEmitter'
import { Element } from '../html/element'

const ZOOM_HORIZONTAL_TRACK_WIDTH = 16

export const ScrollComponent = ({
  children,
  appendStyle,
  autoHeight,
  autoHeightMax,
  autoHeightMin,
  defaultScrollToRight,
  disableScroll,
  horizontalTrackWidth,
  setValueAfterUpdate,
  scrollToBottomCallback,
  scrollToTopCallback,
  scrollToLeftCallback,
  scrollbarsRef,
  scrollLeftValue,
  verticalTrackTop,
  verticalTrackBottom,
  verticalTrackWidth,
  id,
}) => {
  const [isHover, setIsHover] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const [isMouseDown, setIsMouseDown] = useState(false)
  const [isInHorizontalTrack, setIsInHorizontalTrack] = useState(false)
  const [hoverHorizontalTrackWidth, setHoverHorizontalTrackWidth] =
    useState(horizontalTrackWidth)
  const [disable, setDisable] = useState(false)
  const ref = useRef({ scrollTop: 0 })

  const renderTrackHorizontal = ({ style, ...props }) => {
    return (
      <Element
        {...props}
        onMouseEnter={() => {
          setIsInHorizontalTrack(true)
          setHoverHorizontalTrackWidth(ZOOM_HORIZONTAL_TRACK_WIDTH)
        }}
        onMouseLeave={() => {
          setIsInHorizontalTrack(false)
          if (!isScrolling && !isMouseDown) {
            setHoverHorizontalTrackWidth(horizontalTrackWidth)
          }
        }}
        style={{
          ...style,
          width: '100%',
          height: ZOOM_HORIZONTAL_TRACK_WIDTH,
          left: 0,
          bottom: 0,
          opacity: isHover && !disable ? 1 : 0,
          zIndex: 5,
          backgroundColor: 'transparent',
        }}
      />
    )
  }

  const renderTrackVertical = ({ style, ...props }) => {
    return (
      <Element
        {...props}
        style={{
          ...style,
          width: verticalTrackWidth,
          height: `calc(100% - ${verticalTrackTop + verticalTrackBottom}px)`,
          top: verticalTrackTop,
          right: 0,
          opacity: isHover && !disable ? 1 : 0,
          zIndex: 5,
          backgroundColor: 'transparent',
        }}
      />
    )
  }

  const renderThumbHorizontal = ({ style, ...props }) => {
    return (
      <div
        {...props}
        onMouseDownCapture={onMouseDownCapture}
        style={{
          ...style,
          width: '100%',
          height: hoverHorizontalTrackWidth,
          left: 0,
          bottom: 0,
          opacity: isHover && !disable ? 1 : 0,
          zIndex: 5,
          position: 'absolute',
          cursor: 'pointer',
          backgroundColor: '#D9D9D9',
          borderRadius: 12,
        }}
      />
    )
  }

  const renderThumbVertical = ({ style, ...props }) => {
    return (
      <Element
        {...props}
        style={{
          ...style,
          cursor: 'pointer',
          backgroundColor: '#D9D9D9',
          borderRadius: 12,
        }}
      />
    )
  }

  const renderView = ({ style, ...props }) => {
    const customStyle = disable ? { overflow: 'hidden', width: '100%' } : {}
    return (
      <div
        {...props}
        style={{
          ...style,
          ...customStyle,
          zIndex: 0,
          marginRight: style.marginRight ? -17 : 0,
          marginBottom: style.marginBottom ? -17 : 0,
        }}
      />
    )
  }

  const showScroll = () => {
    if (!isHover) {
      setIsHover(true)
    }
  }

  const hideScroll = () => {
    if (isHover) {
      setIsHover(false)
    }
  }

  const removeTimeout = () => {
    if (ref.current.timeOut) {
      clearTimeout(ref.current.timeOut)
      ref.current.timeOut = null
    }
  }

  const onMouseEnter = () => {
    removeTimeout()
    // Wait until finish render content component, check show scroll or not
    ref.current.timeOut = setTimeout(() => {
      showScroll()
    }, 500)
  }

  const onMouseLeave = () => {
    if (!isScrolling) {
      hideScroll()
    }
  }

  const onScroll = (e) => {
    const bottom =
      e?.target.scrollHeight - e?.target.scrollTop <= e?.target.clientHeight

    if (id) {
      if (
        e?.target.scrollTop === 0 &&
        e?.target.scrollHeight !== e?.target.clientHeight
      ) {
        EventEmitter.dispatch(SCROLL_TO_TOP, id)
      }

      if (e?.target.scrollTop > ref.current.scrollTop) {
        EventEmitter.dispatch(SCROLL_DOWN, id)
      }

      ref.current.scrollTop = e?.target.scrollTop
    }

    if (bottom) {
      scrollToBottomCallback()
    }

    if (e?.target.scrollTop === 0) {
      scrollToTopCallback()
    }

    if (e?.target.scrollLeft === 0) {
      scrollToLeftCallback()
    }

    setIsScrolling(true)
    EventEmitter.dispatch(SCROLL, id)
  }

  const onScrollStop = () => {
    setIsScrolling(false)
  }

  const wrappedRef = useRef()

  const onUpdate = (values) => {
    setValueAfterUpdate(values)
  }

  const onMouseUp = (event) => {
    setIsMouseDown(false)
    if (!isInHorizontalTrack) {
      setHoverHorizontalTrackWidth(horizontalTrackWidth)
    }
    if (
      wrappedRef.current &&
      !wrappedRef.current.closest('.scrollbars').contains(event.target)
    ) {
      setIsHover(false)
    }
  }

  const onMouseDownCapture = () => {
    setIsMouseDown(true)
  }

  const scrollRef = useRef(null)

  useEffect(() => {
    if (scrollRef.current && defaultScrollToRight) {
      scrollRef.current.scrollToRight()
    }
  }, [children, scrollLeftValue])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollToTop()
      scrollRef.current.scrollToLeft()
      setDisable(disableScroll)
    }
  }, [disableScroll])

  useEffect(() => {
    if (scrollRef.current && scrollLeftValue) {
      scrollRef.current.scrollLeft(scrollLeftValue)
    }
  }, [scrollLeftValue])

  useEffect(() => {
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [isInHorizontalTrack])

  useEffect(() => {
    return () => {
      removeTimeout()
    }
  }, [])

  useEffect(() => {
    const eventAction = (data) => {
      if (data.id === id) {
        setDisable(data.disable)
      }
    }

    EventEmitter.subscribe(SCROLL_DISABLE, eventAction)
    return () => {
      EventEmitter.unsubscribe(SCROLL_DISABLE, eventAction)
    }
  }, [])

  return (
    <Scrollbars
      ref={scrollbarsRef || scrollRef}
      className="scrollbars"
      style={appendStyle}
      autoHeight={autoHeight}
      autoHeightMax={autoHeightMax}
      autoHeightMin={autoHeightMin}
      renderTrackHorizontal={renderTrackHorizontal}
      renderTrackVertical={renderTrackVertical}
      renderThumbHorizontal={renderThumbHorizontal}
      renderThumbVertical={renderThumbVertical}
      hideTracksWhenNotNeeded
      renderView={renderView}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onScroll={onScroll}
      onScrollStop={onScrollStop}
      onUpdate={onUpdate}
    >
      <div ref={wrappedRef} className="wh100">
        {children}
      </div>
    </Scrollbars>
  )
}

ScrollComponent.propTypes = {
  children: PropTypes.node.isRequired,
  appendStyle: PropTypes.object,
  autoHeight: PropTypes.bool,
  autoHeightMax: PropTypes.number,
  autoHeightMin: PropTypes.number,
  defaultScrollToRight: PropTypes.bool,
  disableScroll: PropTypes.bool,
  horizontalTrackWidth: PropTypes.number,
  setValueAfterUpdate: PropTypes.func,
  scrollbarsRef: PropTypes.object,
  scrollToBottomCallback: PropTypes.func,
  scrollToTopCallback: PropTypes.func,
  scrollToLeftCallback: PropTypes.func,
  scrollLeftValue: PropTypes.number,
  verticalTrackTop: PropTypes.number,
  verticalTrackBottom: PropTypes.number,
  verticalTrackWidth: PropTypes.number,
  id: PropTypes.string,
}

ScrollComponent.defaultProps = {
  appendStyle: {},
  autoHeight: false,
  autoHeightMax: 0,
  autoHeightMin: 0,
  defaultScrollToRight: false,
  disableScroll: false,
  horizontalTrackWidth: 8,
  setValueAfterUpdate: () => {},
  scrollbarsRef: null,
  scrollToBottomCallback: () => {},
  scrollToTopCallback: () => {},
  scrollToLeftCallback: () => {},
  scrollLeftValue: null,
  verticalTrackTop: 0,
  verticalTrackBottom: 0,
  verticalTrackWidth: 8,
}
